import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, apiError, setToken } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { toast } from "sonner";

const Shell = ({ title, subtitle, children }) => (
  <div className="grid min-h-screen lg:grid-cols-2">
    <div className="relative hidden bg-[#0A0A0A] lg:block">
      <img src="https://images.unsplash.com/photo-1513072064285-240f87fa81e8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwxfHxtZWNjYSUyMG1lZGluYSUyMG1vc3F1ZSUyMGthYWJhJTIwaXNsYW1pYyUyMGFyY2hpdGVjdHVyZXxlbnwwfHx8fDE3ODcxMzYwMzd8MA&ixlib=rb-4.1.0&q=85"
        alt="Haramain" className="absolute inset-0 h-full w-full object-cover opacity-35" />
      <div className="relative flex h-full flex-col justify-end p-14">
        
        <h2 className="mt-4 font-display text-4xl font-bold text-white">Portal Mitra & Jamaah</h2>
        <p className="mt-4 max-w-md text-[#D7D0C2]">Satu portal untuk mengelola paket, jadwal, mitra, dokumen, dan laporan.</p>
      </div>
    </div>
    <div className="flex items-center justify-center bg-[#FBF9F4] px-6 py-16">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-3">
          <img src="/logo.png" alt="Royal Saudi Haramain" className="h-10 w-auto" />
        </Link>
        <h1 className="mt-10 font-display text-3xl font-bold text-[#141414]">{title}</h1>
        <p className="mt-2 text-sm text-[#4A4844]">{subtitle}</p>
        {children}
      </div>
    </div>
  </div>
);

const input = "w-full rounded-xl border border-[#E5DEC9] bg-white px-4 py-3 text-sm outline-none focus:border-[#C5A028]";

export function Login() {
  const { login, user } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user) {
      const returnUrl = sessionStorage.getItem("rsh_return_url");
      if (returnUrl) {
        sessionStorage.removeItem("rsh_return_url");
        nav(returnUrl, { replace: true });
      } else {
        nav("/pilih-portal", { replace: true });
      }
    }
  }, [user, nav]);

  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      const u = await login(email, password);
      toast.success(`Selamat datang, ${u.full_name}`);
    } catch (e2) { setErr(apiError(e2)); } finally { setBusy(false); }
  };

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/erp";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <Shell title="Masuk ke Portal" subtitle="Gunakan email internal, akun mitra, atau akun Google Anda.">
      <form onSubmit={submit} className="mt-8 space-y-4">
        <input className={input} type="email" required placeholder="Email" data-testid="login-email-input"
          value={email} onChange={(e) => setEmail(e.target.value)} />
        <input className={input} type="password" required placeholder="Password" data-testid="login-password-input"
          value={password} onChange={(e) => setPassword(e.target.value)} />
        {err && <p data-testid="login-error" className="text-sm text-[#B91C1C]">{err}</p>}
        <button disabled={busy} data-testid="login-submit-btn" className="btn-gold w-full rounded-xl py-3 text-sm font-bold">
          {busy ? "Memproses..." : "Masuk"}
        </button>
        <div className="text-right">
          <Link to="/lupa-password" data-testid="login-forgot-password-link" className="text-sm font-semibold text-[#C5A028] hover:underline">
            Lupa password?
          </Link>
        </div>
      </form>
      <div className="my-6 flex items-center gap-3 text-xs text-[#8A867C]"><span className="h-px flex-1 bg-[#E5DEC9]" /> atau <span className="h-px flex-1 bg-[#E5DEC9]" /></div>
      <button onClick={google} data-testid="login-google-btn"
        className="w-full rounded-xl border border-[#141414] py-3 text-sm font-semibold text-[#141414] transition-colors hover:bg-[#141414] hover:text-white">
        Masuk dengan Google
      </button>
      <p className="mt-6 text-sm text-[#4A4844]">
        Belum punya akun jamaah? <Link to="/register" data-testid="link-register" className="font-bold text-[#C5A028]">Daftar di sini</Link>
      </p>
    </Shell>
  );
}

export function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({ full_name: "", email: "", phone: "", password: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      await register(f);
      toast.success("Pendaftaran berhasil");
      const returnUrl = sessionStorage.getItem("rsh_return_url");
      sessionStorage.removeItem("rsh_return_url");
      nav(returnUrl || "/portal", { replace: true });
    }
    catch (e2) { setErr(apiError(e2)); } finally { setBusy(false); }
  };
  return (
    <Shell title="Daftar Akun Jamaah" subtitle="Buat akun untuk memantau pendaftaran dan dokumen Anda.">
      <form onSubmit={submit} className="mt-8 space-y-4">
        {[["full_name", "Nama lengkap", "text"], ["email", "Email", "email"], ["phone", "Nomor WhatsApp", "text"], ["password", "Password (min. 8 karakter)", "password"]].map(([k, ph, t]) => (
          <input key={k} className={input} type={t} required={k !== "phone"} placeholder={ph}
            data-testid={`register-${k}-input`} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} />
        ))}
        {err && <p data-testid="register-error" className="text-sm text-[#B91C1C]">{err}</p>}
        <button disabled={busy} data-testid="register-submit-btn" className="btn-gold w-full rounded-xl py-3 text-sm font-bold">
          {busy ? "Memproses..." : "Daftar"}
        </button>
      </form>
      <p className="mt-6 text-sm text-[#4A4844]">Sudah punya akun? <Link to="/login" className="font-bold text-[#C5A028]">Masuk</Link></p>
    </Shell>
  );
}

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (e2) { setErr(apiError(e2)); } finally { setBusy(false); }
  };

  return (
    <Shell title="Lupa Password" subtitle="Masukkan email akun Anda. Kami akan mengirim tautan untuk mengatur ulang password.">
      {sent ? (
        <div data-testid="forgot-success" className="mt-8 rounded-2xl border border-[#E5DEC9] bg-white p-6 text-sm text-[#4A4844]">
          <p className="font-semibold text-[#141414]">Tautan reset telah dikirim.</p>
          <p className="mt-2">Jika email <b>{email}</b> terdaftar pada sistem kami, Anda akan menerima email berisi tautan untuk mengatur ulang password. Tautan berlaku selama 1 jam.</p>
          <p className="mt-3 text-xs text-[#8A867C]">Periksa juga folder Spam / Promosi bila email tidak muncul dalam beberapa menit.</p>
          <Link to="/login" className="mt-6 inline-block font-bold text-[#C5A028]">← Kembali ke Masuk</Link>
        </div>
      ) : (
        <>
          <form onSubmit={submit} className="mt-8 space-y-4">
            <input className={input} type="email" required placeholder="Email akun Anda"
              data-testid="forgot-email-input" value={email} onChange={(e) => setEmail(e.target.value)} />
            {err && <p data-testid="forgot-error" className="text-sm text-[#B91C1C]">{err}</p>}
            <button disabled={busy} data-testid="forgot-submit-btn" className="btn-gold w-full rounded-xl py-3 text-sm font-bold">
              {busy ? "Mengirim..." : "Kirim Tautan Reset"}
            </button>
          </form>
          <p className="mt-6 text-sm text-[#4A4844]">
            Ingat password Anda? <Link to="/login" className="font-bold text-[#C5A028]">Kembali ke Masuk</Link>
          </p>
        </>
      )}
    </Shell>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setErr("");
    if (password.length < 8) { setErr("Password minimal 8 karakter"); return; }
    if (password !== confirm) { setErr("Konfirmasi password tidak cocok"); return; }
    if (!token) { setErr("Token reset tidak ditemukan pada tautan"); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      toast.success("Password berhasil diperbarui");
      setTimeout(() => nav("/login", { replace: true }), 1500);
    } catch (e2) { setErr(apiError(e2)); } finally { setBusy(false); }
  };

  if (!token) {
    return (
      <Shell title="Tautan Tidak Valid" subtitle="Tautan reset password tidak lengkap atau sudah kedaluwarsa.">
        <div data-testid="reset-invalid" className="mt-8 rounded-2xl border border-[#E5DEC9] bg-white p-6 text-sm text-[#4A4844]">
          <p>Silakan minta tautan baru melalui halaman lupa password.</p>
          <Link to="/lupa-password" className="mt-4 inline-block font-bold text-[#C5A028]">Minta Tautan Baru →</Link>
        </div>
      </Shell>
    );
  }

  return (
    <Shell title="Setel Password Baru" subtitle="Buat password baru untuk akun Anda. Minimal 8 karakter.">
      {done ? (
        <div data-testid="reset-success" className="mt-8 rounded-2xl border border-[#E5DEC9] bg-white p-6 text-sm text-[#4A4844]">
          <p className="font-semibold text-[#141414]">Password berhasil diperbarui.</p>
          <p className="mt-2">Anda akan diarahkan ke halaman Masuk...</p>
        </div>
      ) : (
        <form onSubmit={submit} className="mt-8 space-y-4">
          <input className={input} type="password" required placeholder="Password baru (min. 8 karakter)"
            data-testid="reset-password-input" value={password} onChange={(e) => setPassword(e.target.value)} />
          <input className={input} type="password" required placeholder="Konfirmasi password baru"
            data-testid="reset-password-confirm-input" value={confirm} onChange={(e) => setConfirm(e.target.value)} />
          {err && <p data-testid="reset-error" className="text-sm text-[#B91C1C]">{err}</p>}
          <button disabled={busy} data-testid="reset-submit-btn" className="btn-gold w-full rounded-xl py-3 text-sm font-bold">
            {busy ? "Menyimpan..." : "Simpan Password Baru"}
          </button>
        </form>
      )}
    </Shell>
  );
}

export function AuthCallback() {
  const { setUser } = useAuth();
  const nav = useNavigate();
  const done = useRef(false);
  const [err, setErr] = useState("");
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const sid = new URLSearchParams(window.location.hash.replace("#", "")).get("session_id");
    (async () => {
      try {
        const { data } = await api.post("/auth/session", {}, { headers: { "X-Session-ID": sid } });
        setToken(data.access_token);
        setUser(data);
        window.history.replaceState(null, "", "/pilih-portal");
        nav("/pilih-portal", { replace: true });
      } catch (e) { setErr(apiError(e)); }
    })();
  }, [nav, setUser]);
  return (
    <div className="flex min-h-screen items-center justify-center bg-[#FBF9F4] text-sm text-[#4A4844]">
      {err ? <div data-testid="oauth-error">{err} — <Link to="/login" className="text-[#C5A028]">kembali ke login</Link></div>
        : <span>Memverifikasi sesi...</span>}
    </div>
  );
}

export function JamaahPortal() {
  const { user, logout } = useAuth();
  const [docs, setDocs] = useState([]);
  useEffect(() => { api.get("/storage/documents").then((r) => setDocs(r.data)).catch(() => {}); }, []);
  return (
    <div className="min-h-screen bg-[#FBF9F4] px-6 py-14">
      <div className="mx-auto max-w-4xl">
        <div className="flex items-center justify-between">
          <div>
            <div className="eyebrow">Portal Jamaah</div>
            <h1 className="mt-2 font-display text-3xl font-bold text-[#141414]">Assalamualaikum, {user?.full_name}</h1>
          </div>
          <button onClick={logout} data-testid="portal-logout-btn" className="rounded-xl border border-[#141414] px-4 py-2 text-sm font-semibold text-[#141414]">Keluar</button>
        </div>
        <div className="mt-8 rounded-2xl border border-[#E5DEC9] bg-white p-6">
          <div className="eyebrow">Profil</div>
          <div className="mt-3 grid gap-2 text-sm text-[#4A4844]">
            <div>Email: <b>{user?.email}</b></div>
            <div>Peran: <b>{user?.role_labels?.join(", ")}</b></div>
          </div>
        </div>
        <div className="mt-6 rounded-2xl border border-[#E5DEC9] bg-white p-6">
          <div className="eyebrow">Dokumen Saya ({docs.length})</div>
          <p className="mt-3 text-sm text-[#8A867C]">Dokumen tersimpan pada penyimpanan privat dan hanya dapat diakses oleh Anda serta petugas berwenang.</p>
        </div>
        <div className="mt-6 flex gap-3">
          <Link to="/cek-booking" className="btn-gold rounded-xl px-5 py-3 text-sm font-bold">Cek Booking</Link>
          <Link to="/" className="rounded-xl border border-[#141414] px-5 py-3 text-sm font-semibold text-[#141414]">Kembali ke Website</Link>
        </div>
      </div>
    </div>
  );
}
